import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../../lib/api";
import { toast } from "sonner";
import { Section, StatusBadge, fmtUsd, fmtDate, EmptyRow } from "./shared";

const ACC_STATUSES = ["pending", "active", "suspended", "replaced", "rejected", "cancelled"];
const VERTICAL_LABEL = { whiteHat: "White-hat", grayHat: "Gray-hat", casino: "Casino & Gambling" };
const SELECT_CLS = "bg-background border border-white/10 rounded-lg px-2 py-1 text-xs";

export function UserAdAccountsTab({ accounts, topups, onChanged }) {
    const setStatus = async (id, status) => {
        try {
            await api.patch(`/admin/ad-accounts/${id}`, { status });
            toast.success("Hesap durumu güncellendi");
            onChanged();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Güncellenemedi");
        }
    };
    const setTopup = async (id, status) => {
        try {
            await api.patch(`/admin/ad-accounts/topups/${id}`, { status });
            toast.success(status === "completed" ? "Yükleme tamamlandı" : "Yükleme reddedildi (iade)");
            onChanged();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Güncellenemedi");
        }
    };

    return (
        <div className="space-y-4">
            <Section title={`Reklam hesapları (${accounts.length})`} testid="user-ad-accounts" action={<Link to="/admin/ad-accounts" className="text-xs text-primary hover:underline">Tüm hesaplar →</Link>}>
                <div className="overflow-x-auto -mx-5">
                    <table className="w-full text-sm min-w-[760px]">
                        <thead className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                            <tr><th className="text-left px-5 py-2">Platform / Hesap</th><th className="text-left py-2">Dikey</th><th className="text-right py-2">Ödenen → Hesaba</th><th className="text-right py-2">Bakiye</th><th className="text-left py-2 pl-4">Durum</th><th className="text-left py-2 pl-4">Tarih</th></tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {accounts.length === 0 && <EmptyRow colSpan={6} text="Reklam hesabı talebi yok." />}
                            {accounts.map((a) => (
                                <tr key={a.id} data-testid={`ad-account-row-${a.id}`}>
                                    <td className="px-5 py-2">
                                        <p className="font-medium capitalize">{a.platform} <span className="text-muted-foreground font-normal">· {a.account_type || "standard"}</span></p>
                                        <p className="text-xs text-muted-foreground">{a.account_name || a.name || "—"}{a.external_id ? ` · ${a.external_id}` : ""}</p>
                                    </td>
                                    <td className="py-2 text-xs">{VERTICAL_LABEL[a.vertical] || a.vertical}</td>
                                    <td className="py-2 text-right text-xs font-mono">
                                        {a.wallet_debit_applied != null ? <>{fmtUsd(a.wallet_debit_applied)} → <span className="text-emerald-300">{fmtUsd(a.net_to_account_applied)}</span><span className="block text-[10px] text-muted-foreground">fee {a.fee_percent_applied}% · {fmtUsd(a.fee_amount_applied)}{a.refunded_at ? " · iade" : ""}</span></> : "—"}
                                    </td>
                                    <td className="py-2 text-right font-semibold">{fmtUsd(a.balance)}</td>
                                    <td className="py-2 pl-4">
                                        <select value={a.status} onChange={(e) => setStatus(a.id, e.target.value)} data-testid={`ad-account-status-${a.id}`} className={SELECT_CLS}>
                                            {ACC_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </td>
                                    <td className="py-2 pl-4 text-xs text-muted-foreground">{fmtDate(a.created_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Section>

            <Section title={`Ek yüklemeler (${topups.length})`} testid="user-topups">
                <div className="overflow-x-auto -mx-5">
                    <table className="w-full text-sm min-w-[640px]">
                        <thead className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                            <tr><th className="text-left px-5 py-2">Tarih</th><th className="text-left py-2">Hesap</th><th className="text-right py-2">Ödenen → Hesaba</th><th className="text-left py-2 pl-4">Durum</th><th className="py-2"></th></tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {topups.length === 0 && <EmptyRow colSpan={5} text="Ek yükleme yok." />}
                            {topups.map((t) => (
                                <tr key={t.id} data-testid={`topup-row-${t.id}`}>
                                    <td className="px-5 py-2 text-xs text-muted-foreground">{fmtDate(t.created_at)}</td>
                                    <td className="py-2 text-xs capitalize">{t.platform} · {t.account_name || "—"}</td>
                                    <td className="py-2 text-right text-xs font-mono">{fmtUsd(t.amount)} → <span className="text-emerald-300">{fmtUsd(t.net_amount)}</span><span className="block text-[10px] text-muted-foreground">fee {t.fee_percent}%</span></td>
                                    <td className="py-2 pl-4"><StatusBadge value={t.status} /></td>
                                    <td className="py-2 pr-5 text-right">
                                        {t.status === "pending" && (
                                            <div className="inline-flex gap-1.5">
                                                <button type="button" onClick={() => setTopup(t.id, "completed")} data-testid={`topup-complete-${t.id}`} className="px-2 py-1 rounded-md text-[11px] bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25">Tamamla</button>
                                                <button type="button" onClick={() => setTopup(t.id, "rejected")} data-testid={`topup-reject-${t.id}`} className="px-2 py-1 rounded-md text-[11px] bg-red-500/15 text-red-300 border border-red-500/30 hover:bg-red-500/25">Reddet</button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Section>
        </div>
    );
}
